import { useEffect, useRef } from "react"
import { useTick } from "@/hooks/useTick"
import type { TimerState } from "@/types"
import { playTimeUpChime } from "@/utils/chime"
import { timerRemainingMs } from "@/utils/timer"

/*
 * The moment the round's clock runs out, as something the room can notice.
 *
 * The clock reaching zero is the one event in a session nobody is looking at
 * when it happens — the facilitator is listening to the room and the room is
 * looking at each other, not at a number in a pill at the bottom of the screen.
 * The amber it turns is a STATE, readable whenever someone next glances down;
 * this is the EVENT, and an event has to arrive.
 *
 * Nothing is stored for it, and nothing is remembered either. The moment a
 * running clock reaches zero is arithmetic on the shared timer — `startedAt`
 * plus what is left to run — so "has it just run out" is derived from the same
 * `now` the countdown renders from, exactly like every other timer reading (see
 * `utils/timer`). A flag flipped by an interval would be one more thing that can
 * disagree with the document, and would have to be unflipped by hand every time
 * the facilitator added a minute.
 *
 * Deriving it also gets the two "do not fire" cases for free. A guest who joins
 * a round whose clock ran out five minutes ago is arriving at a state, not
 * witnessing an event, and so is a backgrounded tab coming forward: both read a
 * moment long past the window and stay quiet.
 */

/**
 * How long the bar stays alarmed.
 *
 * Comfortably longer than a tick, on purpose. `useTick` renders once a second,
 * so the crossing is noticed up to a second late and the window must survive at
 * least one more tick after that or the shake would be cut off mid-shove on
 * unlucky timing. The animation ends itself well inside this; what is left is
 * the class sitting on an element that has stopped moving.
 */
const ALERT_MS = 2500

/**
 * How much is left when the room is told to start landing the round.
 *
 * Two minutes is the last beat in which a facilitator can still change what
 * happens — long enough to close a thought, too short to open one.
 */
export const WRAP_UP_MS = 2 * 60 * 1000

/**
 * The moment this clock is `lead` ahead of zero, or null if it is not heading
 * there. `lead` of 0 is the expiry itself.
 *
 * Only a running clock runs out. A paused one at zero did not cross the line
 * just now — it was already over when it was stopped — and an idle one has no
 * line to cross. `startedAt` is a server timestamp that reads back null until
 * the server acknowledges it, which is also handled here: a clock nobody can
 * place yet has no expiry moment.
 *
 * This answers the EVENT only. Whether the round is inside the window is
 * `insideWindow` below, and the two have to be asked separately — see there.
 */
function crossingMoment(timer: TimerState, lead: number): number | null {
  if (timer.status !== "running" || timer.startedAt === null) return null
  const left = timer.durationMs - timer.elapsedMs
  /*
   * Resuming a clock that was already over restarts it with nothing left to
   * run; that is not a fresh expiry and must not ring the bell again. With a
   * lead this also covers a clock resumed INSIDE the warning window: the
   * crossing happened before the pause, and a bell for it now would be the
   * second one the room heard for the same line.
   */
  if (left <= lead) return null
  return timer.startedAt + left - lead
}

/**
 * Whether this clock is inside its last `lead` and not yet over. `lead` of 0
 * asks whether it is over.
 *
 * Read off what is LEFT rather than from the crossing, because the two answer
 * different questions and a paused clock separates them. Pausing at ninety
 * seconds and resuming puts the crossing in the past — the round is still in
 * its last two minutes, but the moment it entered them is gone, and reading
 * this off `crossingMoment` dropped the WRAP UP label for the rest of the
 * round. It stands during a pause too: a round with ninety seconds left is
 * ending whether or not the clock is currently counting.
 *
 * A round no longer than the warning never raises it at all — a two-minute
 * clock told to wrap up in its first breath is not telling anybody anything.
 */
function insideWindow(timer: TimerState, now: number, lead: number): boolean {
  if (timer.status === "idle" || timer.durationMs <= lead) return false
  const left = timerRemainingMs(timer, now)
  return lead === 0 ? left === 0 : left > 0 && left <= lead
}

/**
 * Whether the bar is alarmed, and — for the wrap-up — whether the round is
 * still inside the window that raised it.
 *
 * `alerting` is the EVENT: true for a beat either side of the crossing, which
 * is what shakes the bar and rings the bell. `within` is the STATE, which is
 * what a standing label reads — and it is derived from what is LEFT rather than
 * from the event, because a clock paused and resumed inside the window has the
 * state without a crossing anyone can still point at.
 */
interface TimerAlert {
  alerting: boolean
  within: boolean
}

function useCrossing(timer: TimerState | null, sound: boolean, lead: number): TimerAlert {
  const now = useTick(timer?.status === "running")

  const crossesAt = timer === null ? null : crossingMoment(timer, lead)
  const since = crossesAt === null ? null : now - crossesAt
  const within = timer !== null && insideWindow(timer, now, lead)
  const alerting = since !== null && since >= 0 && since < ALERT_MS

  const rangFor = useRef<number | null>(null)

  useEffect(() => {
    if (!alerting || rangFor.current === crossesAt) return
    rangFor.current = crossesAt
    if (sound) playTimeUpChime()
  }, [alerting, crossesAt, sound])

  return { alerting, within }
}

/**
 * Returns true for a beat after `timer` runs out, and plays the chime once on
 * the way past when `sound` is set.
 *
 * Pass `null` for a round with no clock — the lobby and the debrief have none.
 */
export function useTimerAlert(timer: TimerState | null, sound: boolean): boolean {
  return useCrossing(timer, sound, 0).alerting
}

/**
 * The same cue, two minutes early: the round is ending and there is still time
 * to land it.
 *
 * Deliberately NOT its own sound. A second bell the room has to learn is a
 * second thing the facilitator has to explain; the shake and the bell say
 * "look at the clock", and the clock — with WRAP UP beside it — says which of
 * the two moments this is.
 *
 */
export function useWrapUpAlert(timer: TimerState | null, sound: boolean): TimerAlert {
  return useCrossing(timer, sound, WRAP_UP_MS)
}
